import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { createGTMTracking, getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import { goToLandingSection } from '../../utils/landingNav';
import { subscribeToNewsletter } from '../../services/newsletterService';
import { Button } from '@/components/ui/atoms/Button';

const socialLinks = [
  // {
  //   name: 'X (Twitter)',
  //   href: 'https://twitter.com/in-virtus',
  //   icon: (
  //     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  //       <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor"/>
  //     </svg>
  //   ),
  // }, // Hidden for now, can be restored later
  {
    name: 'LinkedIn',
    href: 'https://linkedin.com/company/in-virtus',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <text x="8" y="14" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#171D27" fontFamily="Inter, sans-serif">in</text>
      </svg>
    ),
  },
];


export default function Footer() {
  const { t } = useTranslation('marketing');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const navigationItems = useMemo(
    () => [
      { label: t('nav.how_it_works'), href: '#how-it-works', target: 'how_it_works' as const },
      { label: t('nav.pricing'), href: '#pricing', target: 'pricing' as const },
    ],
    [t],
  );

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError(t('footer.email_required'));
      return;
    }

    if (!validateEmail(email)) {
      setError(t('footer.email_invalid'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      console.log('Subscribing to newsletter:', email);

      // Submit newsletter subscription to backend
      const response = await subscribeToNewsletter(email);

      if (response.data && response.data.success) {
        toast.success(response.data.message);
        setEmail('');
        console.log('Newsletter subscription successful');
      } else {
        const errorMessage = response.error || t('footer.subscribe_failed');
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);

      const errorMessage = error instanceof Error
        ? error.message
        : t('footer.subscribe_error');

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavClick = (href: string) => {
    goToLandingSection(href, navigate, pathname);
  };

  return (
    <section {...getGTMAttributes(createGTMTracking('SECTION_VIEWED', { origin: 'landing_page', target: 'footer' }))}>
      <footer className="flex w-full flex-col justify-center items-start gap-4 sm:gap-5 relative">
        <div className="flex min-h-[300px] sm:min-h-[400px] flex-col justify-center items-start gap-6 sm:gap-8 lg:gap-10 w-full relative overflow-hidden px-4 sm:px-6 lg:px-20 py-8 sm:py-12" style={{
          background: `
              radial-gradient(circle at top right, var(--color-gradient-pink) 0%, transparent 15%),
              radial-gradient(circle at bottom  calc(-10% - 200px) right calc(30% - 20px), var(--color-gradient-purple) 1%, transparent 40%),
              linear-gradient(135deg, var(--color-light-lin) 0%, var(--color-gradient-light) 100%)
            `
        }}>
          <div className="flex h-auto sm:h-[218px] justify-between items-start sm:items-end shrink-0 w-full relative z-[4] flex-col sm:flex-row gap-6 sm:gap-8 lg:gap-10">
            <div className="flex flex-col justify-center items-start gap-4 sm:gap-6 relative w-full sm:w-auto">
              <img src="/logo/dark-logo.png" alt="Invirtus" className="w-12 h-12 sm:w-[60px] sm:h-[60px] relative" />

              <h2 className="text-[#171D27] text-xl sm:text-2xl lg:text-3xl font-semibold leading-tight sm:leading-[28px] lg:leading-[31.2px] tracking-[-0.12px] relative">
                {t('footer.subscribe_line1')} <br /> {t('footer.subscribe_line2')}
              </h2>

              <div className="relative w-full sm:w-[280px] lg:w-[312px]">
                <form
                  onSubmit={handleSubmit}
                  className={`flex w-full h-10 sm:h-12 justify-between items-center relative bg-white pl-3 sm:pl-4 pr-1 py-1 rounded-[80px] transition-all duration-200 ${error
                      ? 'ring-2 ring-red-500 ring-opacity-50 shadow-lg shadow-red-500/20'
                      : 'hover:shadow-md focus-within:shadow-lg focus-within:ring-2 focus-within:ring-purple/30'
                    }`}
                  noValidate
                  {...getGTMAttributes(createGTMTracking('SUBSCRIBE_NEWSLETTER', { origin: 'footer' }))}
                >
                  <div className="flex-1 relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError('');
                      }}
                      placeholder={t('footer.email_placeholder')}
                      className={`w-full bg-transparent text-[#171D27] text-xs sm:text-sm font-normal placeholder:opacity-60 outline-none transition-colors duration-200 ${error ? 'text-red-600' : ''
                        }`}
                      aria-label={t('footer.email_aria')}
                      aria-describedby={error ? 'email-error' : undefined}
                      aria-invalid={error ? 'true' : 'false'}
                    />
                    {error && (
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="text-red-500"
                        >
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                          <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2" />
                          <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2" />
                        </svg>
                      </div>
                    )}
                  </div>

                  <Button
                    label={t('footer.send')}
                    htmlType="submit"
                    type="contained"
                    intent="secondary"
                    loading={isSubmitting}
                    aria-label={t('footer.subscribe_aria')}
                  />
                </form>

                {/* Error message with improved styling */}
                {error && (
                  <div
                    id="email-error"
                    className="absolute top-full left-0 right-0 mt-2 px-3 py-1 bg-red-50 border border-red-200 rounded-lg shadow-sm animate-in slide-in-from-top-2 duration-200"
                    role="alert"
                  >
                    <div className="flex items-start gap-2">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-red-500 mt-0.5 flex-shrink-0"
                      >
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" />
                        <circle cx="12" cy="16" r="1" fill="currentColor" />
                      </svg>
                      <span className="text-red-700 text-xs sm:text-sm font-medium">
                        {error}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <nav className="flex h-auto sm:h-[134px] flex-col justify-start sm:justify-end items-start gap-3 sm:gap-4 lg:gap-5 relative w-full sm:w-auto">
              {navigationItems.map((item) => (
                <Button
                  key={item.label}
                  type="text"
                  intent="secondary"
                  size="sm"
                  onClick={() => handleNavClick(item.href)}
                  label={item.label}
                  className="!px-0 text-left sm:text-center font-semibold hover:border-b-2 hover:border-[#171D27] rounded-none"
                  aria-label={t('footer.nav_aria', { label: item.label })}
                  {...getGTMAttributes(createGTMTracking('NAVIGATE_SECTION', { origin: 'footer', target: item.target }))}
                />
              ))}
            </nav>
          </div>

          {/* CNDP / Law 09-08 notice — subtle, full-width responsive */}
          <p className="w-full max-w-full sm:max-w-[520px] lg:max-w-[640px] text-[10.5px] sm:text-[11px] leading-relaxed text-dark-invirtus/55 relative z-[4]">
            {t('footer.cndp_notice')}{' '}
            <a
              href="/privacy"
              className="underline decoration-dark-invirtus/30 underline-offset-2 hover:decoration-dark-invirtus/70"
            >
              {t('footer.privacy_link')}
            </a>
            .
          </p>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-6 w-full relative z-[4] pt-6 sm:pt-8 px-0 border-t-[#E1D2DA] border-t border-solid">
            <div className="flex items-center gap-4 text-[#171D27] text-xs sm:text-sm leading-4 sm:leading-[21px]">
              <a href="/terms" className="opacity-60 hover:opacity-100 hover:underline transition-all">{t('footer.terms')}</a>
              <span className="opacity-30">|</span>
              <a href="/privacy" className="opacity-60 hover:opacity-100 hover:underline transition-all">{t('footer.privacy')}</a>
            </div>

            <div className="flex items-center gap-4 sm:gap-6">
              <div className="text-[#171D27] text-xs sm:text-sm font-normal leading-4 sm:leading-[21px] opacity-60">
                {t('footer.copyright')}
              </div>
              <div className="flex items-center gap-2">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-7 h-7 sm:w-8 sm:h-8 items-center justify-center backdrop-blur-lg relative bg-[#DCB3FF] p-1.5 sm:p-2 rounded-lg hover:bg-[#D1A3FF] transition-colors"
                    aria-label={t('footer.social_aria', { name: social.name })}
                    {...getGTMAttributes(createGTMTracking('SOCIAL_CLICK', { origin: 'footer', target: social.name.toLowerCase() }))}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </section>
  );
} 